// region 感知 hook —— 一次性从后端 /api/internal/region 拉当前部署的 region，
// 用于 cn 部署 vs apac 部署的 UI 降级（隐藏对方 region 不存在的模块）。
//
// 设计：
// - 模块级缓存（启动后只 fetch 一次，进程内复用）
// - 同步初值从 localStorage 读，避免首屏 flicker；异步刷新到最新
// - 失败时 fallback 到 'cn'（最宽松显示）
//
// 后端配置见 config.local.yaml: ulucu.region (cn | apac)。
// 移动海外包可用 VITE_ULUCU_REGION=apac 做构建级覆盖，避免同一生产网关返回 cn 时串到国内 UI。
import { useEffect, useState } from 'react';
import { apiUrl } from '../utils/api-base.js';

export type Region = 'cn' | 'apac';

const CACHE_KEY = 'ulucu.backend.region';
const CONFIGURED_REGION = normalizeRegion(
	import.meta.env.VITE_ULUCU_REGION
	?? import.meta.env.VITE_MOBILE_REGION
	?? import.meta.env.VITE_APP_REGION,
);
let _cached: Region | null = null;
let _inflight: Promise<Region> | null = null;
const listeners = new Set<(region: Region) => void>();

function normalizeRegion(value: unknown): Region | null {
	return value === 'apac' || value === 'cn' ? value : null;
}

function setCachedRegion(region: Region): void {
	_cached = region;
	try { localStorage.setItem(CACHE_KEY, region); } catch { /* quota / SSR */ }
	listeners.forEach((listener) => listener(region));
}

function subscribeRegion(listener: (region: Region) => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function readCache(): Region | null {
	if (CONFIGURED_REGION) return CONFIGURED_REGION;
	if (_cached) return _cached;
	if (typeof localStorage === 'undefined') return null;
	const v = localStorage.getItem(CACHE_KEY);
	if (v === 'cn' || v === 'apac') { _cached = v; return v; }
	return null;
}

async function fetchRegion(): Promise<Region> {
	if (CONFIGURED_REGION) {
		setCachedRegion(CONFIGURED_REGION);
		return CONFIGURED_REGION;
	}
	if (_inflight) return _inflight;
	_inflight = (async () => {
		try {
			const res = await fetch(apiUrl('/api/internal/region'));
			const j = await res.json();
			const r: Region = j.region === 'apac' ? 'apac' : 'cn';
			setCachedRegion(r);
			return r;
		} catch {
			setCachedRegion('cn');
			return 'cn';
		} finally {
			_inflight = null;
		}
	})();
	return _inflight;
}

/**
 * 取当前后端 region。
 * - 第一次调用时返回缓存值（可能是 null → 'cn' fallback），同时异步去 fetch
 * - fetch 完会触发 setState 让组件 re-render
 */
export function useRegion(): Region {
	const [region, setRegion] = useState<Region>(() => readCache() ?? 'cn');
	useEffect(() => {
		const unsubscribe = subscribeRegion(setRegion);
		const cached = readCache();
		if (cached) setRegion(cached);
		void fetchRegion().then(setRegion);
		return unsubscribe;
	}, []);
	return region;
}

/** 同步取，仅用于已知 region 已 fetch 过的场景（非 React 调用） */
export function getRegionSync(): Region {
	return readCache() ?? 'cn';
}

/** 构建级 region 覆盖；海外 APK 用，普通 Web 为空时继续听后端。 */
export function getConfiguredRegion(): Region | null {
	return CONFIGURED_REGION;
}
